import { useState, useMemo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, ChevronLeft, ChevronRight, X } from "lucide-react";

const ALL = "__all__";

// config: { searchFields: [key|fn], filters: {key:{label, get:(it)=>val}}, sortOptions:[{value,label,cmp}], pageSize }
export function useListControls(items, config = {}) {
  const { searchFields = [], filters = {}, sortOptions = [], pageSize = 9 } = config;
  const [query, setQuery] = useState("");
  const [filterState, setFilterState] = useState(() =>
    Object.fromEntries(Object.keys(filters).map((k) => [k, ALL]))
  );
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);

  const get = (it, f) => (typeof f === "function" ? f(it) : it?.[f]);

  const filtered = useMemo(() => {
    let out = items || [];
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((it) =>
        searchFields.some((f) => String(get(it, f) ?? "").toLowerCase().includes(q))
      );
    }
    for (const [key, cfg] of Object.entries(filters)) {
      const val = filterState[key];
      if (val && val !== ALL) out = out.filter((it) => String(cfg.get(it) ?? "") === val);
    }
    const so = sortOptions.find((s) => s.value === sort);
    if (so?.cmp) out = [...out].sort(so.cmp);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, filterState, sort]);

  useEffect(() => { setPage(1); }, [query, filterState, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const visible = filtered.slice((curPage - 1) * pageSize, curPage * pageSize);

  const setFilter = (key, val) => setFilterState((s) => ({ ...s, [key]: val }));

  return {
    query, setQuery, filterState, setFilter, sort, setSort,
    filtered, visible, page: curPage, setPage, totalPages, pageSize,
    filtersConfig: filters, sortOptions, items,
  };
}

export function ListToolbar({ controls, searchPlaceholder = "Cerca…" }) {
  const { query, setQuery, filterState, setFilter, sort, setSort, filtersConfig, sortOptions, items } = controls;

  const optionsFor = (key, cfg) => {
    const set = new Set();
    (items || []).forEach((it) => {
      const v = cfg.get(it);
      if (v != null && String(v).trim() !== "") set.add(String(v));
    });
    return [...set].sort();
  };

  const hasFilters = Object.keys(filtersConfig).length > 0;
  const active = query || Object.values(filterState).some((v) => v !== ALL) || sort !== "default";

  return (
    <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="list-toolbar">
      <div className="relative flex-1 min-w-[220px]">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="list-search-input"
          className="pl-9 rounded-none"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {hasFilters && Object.entries(filtersConfig).map(([key, cfg]) => (
        <Select key={key} value={filterState[key]} onValueChange={(v) => setFilter(key, v)}>
          <SelectTrigger data-testid={`list-filter-${key}`} className="w-[180px] rounded-none">
            <SelectValue placeholder={cfg.label} />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value={ALL}>{cfg.label}: tutti</SelectItem>
            {optionsFor(key, cfg).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ))}

      {sortOptions.length > 0 && (
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="list-sort" className="w-[180px] rounded-none">
            <SelectValue placeholder="Ordina" />
          </SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="default">Ordine predefinito</SelectItem>
            {sortOptions.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {active && (
        <Button variant="ghost" size="sm" className="rounded-none" data-testid="list-clear-filters"
          onClick={() => { setQuery(""); Object.keys(filtersConfig).forEach((k) => setFilter(k, ALL)); setSort("default"); }}>
          <X className="w-4 h-4 mr-1" /> Azzera
        </Button>
      )}
    </div>
  );
}

export function ListPagination({ controls }) {
  const { page, setPage, totalPages, filtered, pageSize } = controls;
  if (filtered.length === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, filtered.length);
  return (
    <div className="flex items-center justify-between mt-6" data-testid="list-pagination">
      <div className="text-xs text-muted-foreground font-mono" data-testid="list-pagination-info">
        {from}–{to} di {filtered.length}
      </div>
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="rounded-none" data-testid="list-prev-page"
            disabled={page <= 1} onClick={() => setPage(page - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs font-mono px-3" data-testid="list-page-indicator">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" className="rounded-none" data-testid="list-next-page"
            disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
